"use client";

import { useState, useRef } from "react";
import { Box, Avatar, CircularProgress, Link } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  ConfirmButton,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import RichTextAIField, { isHtmlEmpty } from "@components/shared/form/ai/RichTextAIField/RichTextAIField";
import { createRecruitingPrompt } from "@/lib/agent/utils/candidateToPrompt";
import FormSection from "./parts/FormSection";
import FormFieldTitle from "./parts/FormFieldTitle";

type EducationImageProps = React.ComponentProps<typeof Avatar> & { themeColor?: string };

const EducationImage = ({ themeColor, ...props }: EducationImageProps) => (
  <Avatar
    {...props}
    sx={{
      width: "84px",
      height: "84px",
      borderRadius: "50%",
      overflow: "hidden",
      alignSelf: "flex-start",
      backgroundColor: themeColor ? alpha(themeColor, 0.4) : "#e0e7ff",
      // MUI Avatar's default fallback text color is near-white, which fails
      // contrast against this light lavender/pastel background — a fixed
      // dark navy reads clearly against both the fallback and any
      // alpha(themeColor, 0.4) tint, since 0.4 alpha over a light backdrop
      // always lands pale regardless of hue.
      color: "var(--primitive-indigo-600, #13319a)",
      fontSize: "32px",
      fontWeight: "600",
    }}
  />
);

interface Education {
  id?: number | string;
  course_name?: string | null;
  institution?: string | null;
  year_completed?: string | null;
  description?: string | null;
  logo?: string | null;
  [key: string]: unknown;
}

interface EditEducationProps {
  onClose: () => void;
  open: boolean;
  education?: Education | null;
  onUpdate: (education: Record<string, unknown>) => void;
  onDelete: (id: Education["id"]) => void;
  candidate: unknown;
}

export default function EditEducation({
  onClose,
  open,
  education,
  onUpdate,
  onDelete,
  candidate,
}: EditEducationProps) {
  const [educationData, setEducationData] = useState({
    course_name: education?.course_name || "",
    institution: education?.institution || "",
    year_completed: education?.year_completed || "",
    description: education?.description || "",
    logo: education?.logo || "",
    image_data: null as File | null,
  });
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEducationData({ ...educationData, [event.target.name]: event.target.value });
  };

  const handleSubmit = () => {
    // The same rich-text editor the profile's roles and projects use; a
    // cleared editor leaves "<p></p>" behind, which is saved as empty.
    const description = isHtmlEmpty(educationData.description) ? "" : educationData.description;
    onUpdate({ ...education, ...educationData, description });
    onClose();
  };

  const handleUploadImageClick = (event: React.MouseEvent) => {
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const handleImageRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    setEducationData((prev) => ({ ...prev, logo: "", image_data: null }));
  };

  const handleUpdateImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setEducationData((prev) => ({ ...prev, logo: String(reader.result ?? ""), image_data: file }));
      setImageLoading(false);
    };
    reader.onerror = () => setImageLoading(false);
    reader.readAsDataURL(file);
  };

  const handleDeleteEducation = () => {
    onDelete(education?.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit education details</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <FormSection>
              <FormFieldTitle>Course name:</FormFieldTitle>
              <TextField fullWidth name="course_name" variant="outlined" value={educationData.course_name} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Institution:</FormFieldTitle>
              <TextField fullWidth name="institution" variant="outlined" value={educationData.institution} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Year completed:</FormFieldTitle>
              <TextField fullWidth name="year_completed" variant="outlined" value={educationData.year_completed} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Description:</FormFieldTitle>
              <RichTextAIField
                id="education-description"
                value={educationData.description}
                onChange={(value) => setEducationData((prev) => ({ ...prev, description: value }))}
                placeholder="Describe your education and key learnings..."
                alwaysShowToolbar
                minHeight={96}
                prompt={
                  createRecruitingPrompt(candidate, "general") +
                  `. This is an education description for "${educationData.course_name || "course"}" at "${educationData.institution || "institution"}" completed in "${educationData.year_completed || "year"}". Write a compelling description that highlights relevant coursework, key learnings, projects, achievements, skills gained, and how this education contributes to the candidate's professional qualifications. Make it relevant for recruiting purposes.`
                }
              />
            </FormSection>
            <FormSection sx={{ flexDirection: "row", display: "flex", gap: "16px" }}>
              <FormFieldTitle>Institution logo:</FormFieldTitle>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "center" }}>
                {!imageLoading && (
                  <EducationImage src={educationData.logo} alt={educationData.institution}>
                    {educationData.institution.substring(0, 2)}
                  </EducationImage>
                )}
                {imageLoading && <CircularProgress size={80} color="secondary" />}
                {!imageLoading && !educationData.logo && (
                  <Link href="#" onClick={handleUploadImageClick}>Upload image</Link>
                )}
                {!imageLoading && educationData.logo && (
                  <Link href="#" onClick={handleImageRemove}>Remove</Link>
                )}
                <input hidden accept="image/*" type="file" onChange={handleUpdateImage} ref={fileInputRef} />
              </Box>
            </FormSection>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between" }}>
            <ConfirmButton
              text="Delete education"
              onUpdate={handleDeleteEducation}
              variant="tertiary"
              color="red"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
